"use client";
import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { useRouter, usePathname } from "next/navigation";
import { graphQlFetchFunction } from "@packages/lib/server-actions/server-action";
import { subjectGuideAjax } from "@packages/lib/graphQL/theme-landing";
import { GA4DataLayer } from "@packages/lib/utlils/helper-function";
import { GAConstants } from "@packages/shared-components/services/constants";

interface SubjectGuide {
  pageTitle: string;
  urlSlug: string;
}

interface AdviceSearchProps {
  placeholder?: string;
}

const AdviceSearchTab: React.FC<AdviceSearchProps> = ({ placeholder }) => {
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [searchText, setSearchText] = useState("");
  const [selectedUrl, setSelectedUrl] = useState("");
  const [subjects, setSubjects] = useState<SubjectGuide[]>([]);
  const [dropdown, setDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [subjecterror, setSubjecterror] = useState(false);
  const pathname = usePathname();

  // Reset loading state when pathname changes (navigation complete or browser back)
  useEffect(() => {
    setIsLoading(false);
  }, [pathname]);

  useEffect(() => {
    const fetchSubjects = async () => {
      const query = subjectGuideAjax();
      const data = await graphQlFetchFunction(query, false);
      const items = data?.data?.articleCollection?.items || [];
      const unique = items.filter(
        (item: SubjectGuide, index: number, self: SubjectGuide[]) =>
          self.findIndex((s) => s.urlSlug === item.urlSlug) === index,
      );
      unique.sort((a: SubjectGuide, b: SubjectGuide) =>
        a.pageTitle.localeCompare(b.pageTitle),
      );
      setSubjects(unique);
    };
    fetchSubjects();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = subjects.filter((s) =>
    s.pageTitle.toLowerCase().includes(searchText.toLowerCase()),
  );

  return (
    <div className="relative flex flex-col max-w-[705px]">
      <div
        ref={containerRef}
        className="bg-white rounded-[24px] p-[16px] border border-grey-200 hover:border-primary-500 shadow-custom-1 md:rounded-[32px] md:pl-[24px] md:p-[10px]"
      >
        <div className="flex flex-col small md:flex-row md:items-center">
          <div className="w-full relative grow">
            <input
              autoComplete="off"
              value={searchText}
              type="text"
              data-text-id="subject_search_input"
              className="w-full focus:outline-none text-black placeholder:text-gray-500 px-[0] py-[12px] md:px-[16px] md:py-[10px]"
              aria-label="submenu"
              placeholder={placeholder || "Enter Subject"}
              onChange={(e) => {
                setSearchText(
                  e.target.value.replace(/\s{2,}/g, " ").trimStart(),
                );
                setSelectedUrl("");
                setDropdown(true);
                setSubjecterror(false);
              }}
              onClick={() => setDropdown(true)}
            />
            {dropdown && filtered.length > 0 && (
              <div className="bg-white z-[1] shadow-custom-3 rounded-[4px] absolute left-0 top-full w-full max-h-[310px] overflow-y-auto custom-scrollbar-2">
                {subjecterror && (
                  <p className="small text-negative-default px-[16px] pt-[12px]">
                    Please select a subject
                  </p>
                )}
                <ul>
                  {filtered.map((item, index) => (
                    <li
                      key={index}
                      onClick={() => {
                        setSearchText(item.pageTitle);
                        setSelectedUrl(item.urlSlug);
                        setDropdown(false);
                        setSubjecterror(false);
                      }}
                      className="block small px-[16px] py-[12px] hover:bg-blue-50 hover:underline cursor-pointer"
                    >
                      {item.pageTitle}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="pt-[12px] md:pt-[0]">
            <button
              type="button"
              data-text-id="search_submit_button"
              disabled={isLoading}
              className="btn btn-primary flex items-center justify-center gap-[6px] px-[24px] py-[10px] w-full md:w-[114px]"
              onClick={() => {
                if (selectedUrl) {
                  setIsLoading(true);
                  setSubjecterror(false);
                  GA4DataLayer({
                    event: GAConstants.track_event,
                    event_name: "search_event",
                    page_section: "subject_guides",
                    subject_area: searchText,
                    search_type: GAConstants.subjectGuideSearchType,
                  });
                  router.push(selectedUrl);
                } else {
                  setSubjecterror(true);
                  if (filtered.length > 0) {
                    setDropdown(true);
                  }
                }
              }}
            >
              {!isLoading && (
                <Image
                  src="/static/assets/icons/search_icon.svg"
                  width="18"
                  height="18"
                  alt="Search icon"
                />
              )}
              Search
              {isLoading && (
                <svg
                  className="animate-spin h-[18px] w-[18px]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
      {subjecterror && !(dropdown && filtered.length > 0) && (
        <p className="absolute top-full left-[10px] small text-negative-default px-[16px] md:px-[24px] mt-[8px]">
          Please select a subject
        </p>
      )}
    </div>
  );
};

export default AdviceSearchTab;
